import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import NovariMark from "./NovariMark";
import {
  MARK_BIG_D,
  MARK_BIG_FILL_D,
  MARK_CENTER,
  MARK_FULL_D,
  MARK_SMALL_D,
  MARK_SMALL_FILL_D,
  MARK_VIEW_BOX,
} from "./novariMarkPath";

interface NovariMarkLoaderProps {
  /** Pixel size of the glyph's bounding box. */
  size?: number;
  color?: string;
  className?: string;
  /**
   * Re-pick a random variant every few seconds while mounted, so a loading
   * screen that lingers doesn't sit on the same motion the whole time.
   * Defaults on for the full-page loading screens; set false for short,
   * inline spinners where a mid-flight variant swap would be distracting.
   */
  cycle?: boolean;
}

const VARIANT_COUNT = 15;

function Svg({ size, children }: { size: number; children: ReactNode }) {
  return (
    <svg width={size} height={size} viewBox={MARK_VIEW_BOX} fill="none" style={{ overflow: "visible" }} aria-hidden>
      {children}
    </svg>
  );
}

/** The two lobe strokes, drawn from the waist (the glyph's visual center)
 * outward to each lobe at once — literally "drawn from the middle out". */
function DrawStrokes({
  color,
  strokeWidth = 9,
  className,
  smallDelay = 0.12,
}: {
  color: string;
  strokeWidth?: number;
  className: string;
  smallDelay?: number;
}) {
  return (
    <>
      <path
        d={MARK_BIG_D}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        pathLength={1}
        strokeDasharray="1 1"
        className={className}
      />
      <path
        d={MARK_SMALL_D}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        pathLength={1}
        strokeDasharray="1 1"
        className={className}
        style={{ animationDelay: `${smallDelay}s` }}
      />
    </>
  );
}

function FillMark({ color, className, style }: { color: string; className?: string; style?: CSSProperties }) {
  return <path d={MARK_FULL_D} fill={color} className={className} style={style} />;
}

function Pieces({
  color,
  bigClassName,
  smallClassName,
}: {
  color: string;
  bigClassName: string;
  smallClassName: string;
}) {
  return (
    <>
      <path d={MARK_BIG_FILL_D} fill={color} className={bigClassName} />
      <path d={MARK_SMALL_FILL_D} fill={color} className={smallClassName} />
    </>
  );
}

function CenterDot({ color, className }: { color: string; className: string }) {
  return <circle cx={MARK_CENTER.x} cy={MARK_CENTER.y} r={7} fill={color} className={className} />;
}

function Rings({ color, count = 2, className, delayStep = 0.6 }: { color: string; count?: number; className: string; delayStep?: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <circle
          key={i}
          cx={MARK_CENTER.x}
          cy={MARK_CENTER.y}
          r={26}
          fill="none"
          stroke={color}
          strokeWidth={2}
          className={className}
          style={{ animationDelay: `${i * delayStep}s` }}
        />
      ))}
    </>
  );
}

function renderVariant(n: number, size: number, color: string): ReactNode {
  switch (n) {
    case 1: // Draw Center-Out — the signature motion: both lobes draw
            // outward from the waist, then settle into the solid mark.
      return (
        <Svg size={size}>
          <DrawStrokes color={color} className="novarimark-anim-draw-then-fade" />
          <FillMark color={color} className="novarimark-anim-fill-in" />
        </Svg>
      );
    case 2: // Draw Center-Out, Fast
      return (
        <Svg size={size}>
          <DrawStrokes color={color} strokeWidth={10} className="novarimark-anim-draw-then-fade-fast" />
          <FillMark color={color} className="novarimark-anim-fill-in-fast" />
        </Svg>
      );
    case 3: // Center Spark — a bright core flashes first, as if the pen
            // touched down, before the two strokes sweep outward.
      return (
        <Svg size={size}>
          <DrawStrokes color={color} className="novarimark-anim-draw" />
          <FillMark color={color} className="novarimark-anim-fill-in" />
          <CenterDot color={color} className="novarimark-anim-center-flash" />
        </Svg>
      );
    case 4: // Draw + Ripple — drawing outward while two rings expand from
            // the same center point.
      return (
        <Svg size={size}>
          <Rings color={color} className="novarimark-anim-ring" />
          <DrawStrokes color={color} className="novarimark-anim-draw-then-fade" />
          <FillMark color={color} className="novarimark-anim-fill-in" />
        </Svg>
      );
    case 5: // Deconstruct / Reconstruct — the mark splits into its two
            // lobes, drifts apart, and comes back together.
      return (
        <Svg size={size}>
          <Pieces
            color={color}
            bigClassName="novarimark-anim-piece-apart-big"
            smallClassName="novarimark-anim-piece-apart-small"
          />
        </Svg>
      );
    case 6: // Breathe — a calm, low-motion hold for screens that resolve
            // almost instantly.
      return (
        <Svg size={size}>
          <Rings color={color} count={1} className="novarimark-anim-ring" />
          <FillMark color={color} className="novarimark-anim-breathe" />
        </Svg>
      );
    case 7: { // Rotate Draw — draws in, then the whole glyph turns once.
      return (
        <Svg size={size}>
          <g className="novarimark-anim-rotate">
            <DrawStrokes color={color} className="novarimark-anim-draw-then-fade" />
            <FillMark color={color} className="novarimark-anim-fill-in" />
          </g>
        </Svg>
      );
    }
    case 8: // Spring In — a quick, elastic pop rather than a literal draw.
      return (
        <Svg size={size}>
          <FillMark color={color} className="novarimark-anim-spring-in" />
        </Svg>
      );
    case 9: // Split, Spin, Reform — both lobes give a small counter-spin
            // before settling back into place.
      return (
        <Svg size={size}>
          <Pieces
            color={color}
            bigClassName="novarimark-anim-piece-spin-big"
            smallClassName="novarimark-anim-piece-spin-small"
          />
        </Svg>
      );
    case 10: { // Orbit + Draw — a small satellite dot circles the glyph
               // while it draws itself in.
      return (
        <Svg size={size}>
          <DrawStrokes color={color} className="novarimark-anim-draw-then-fade" />
          <FillMark color={color} className="novarimark-anim-fill-in" />
          <g className="novamark-anim-orbit" style={{ transformOrigin: `${MARK_CENTER.x}px ${MARK_CENTER.y}px` }}>
            <circle cx={MARK_CENTER.x} cy={MARK_CENTER.y - 60} r={4} fill={color} />
          </g>
        </Svg>
      );
    }
    case 11: // Wobble Settle — draws in with a little playful jitter as it
             // lands, like a hand finishing a stroke.
      return (
        <Svg size={size}>
          <g className="novarimark-anim-wobble-settle">
            <DrawStrokes color={color} className="novarimark-anim-draw" />
            <FillMark color={color} className="novarimark-anim-fill-in" />
          </g>
        </Svg>
      );
    case 12: // Fade Trace — a faint, slow, minimal outline trace. The
             // quietest variant, for moments that shouldn't shout.
      return (
        <Svg size={size}>
          <DrawStrokes color={color} strokeWidth={5} className="novarimark-anim-draw" />
          <FillMark color={color} className="novarimark-anim-fill-in" style={{ opacity: 0.92 }} />
        </Svg>
      );
    case 13: // Center Burst — the same spark-then-draw idea as #3, but
             // thinner strokes and a bigger flash for a punchier feel.
      return (
        <Svg size={size}>
          <CenterDot color={color} className="novarimark-anim-center-flash" />
          <DrawStrokes color={color} strokeWidth={7} className="novarimark-anim-draw-then-fade" />
          <FillMark color={color} className="novarimark-anim-fill-in" />
        </Svg>
      );
    case 14: // Assemble While Drawing — the two lobes drift together as a
             // faint outline traces the seam between them.
      return (
        <Svg size={size}>
          <DrawStrokes color={color} strokeWidth={4} className="novarimark-anim-draw" />
          <Pieces
            color={color}
            bigClassName="novarimark-anim-piece-apart-big"
            smallClassName="novarimark-anim-piece-apart-small"
          />
        </Svg>
      );
    case 15: // Calm Ring — mark stays put, a single slow ring breathes
    default: // outward around it. The lightest-weight variant of the set.
      return (
        <Svg size={size}>
          <Rings color={color} count={2} delayStep={1.1} className="novarimark-anim-ring" />
          <FillMark color={color} />
        </Svg>
      );
  }
}

/**
 * The animated Novari mark — the brand glyph drawing itself in from its own
 * center outward to both lobes, in the spirit of a hand finishing a single
 * confident stroke. Used everywhere the app previously showed a generic
 * spinner or the full-page loading screen: one of fifteen variations is
 * picked at random on mount, and (when `cycle` is on) re-picked every few
 * seconds so a loading screen that lingers keeps feeling alive instead of
 * stuck. Falls back to the plain static NovariMark for prefers-reduced-motion.
 */
export default function NovariMarkLoader({
  size = 64,
  color = "var(--accent-primary)",
  className,
  cycle = true,
}: NovariMarkLoaderProps) {
  const reduceMotion = useReducedMotion();
  const [variant, setVariant] = useState(() => 1 + Math.floor(Math.random() * VARIANT_COUNT));

  useEffect(() => {
    if (reduceMotion || !cycle) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const scheduleNext = () => {
      // 4.5–7.5s, randomized so cycles across the page never sync up.
      const delay = 4500 + Math.random() * 3000;
      timer = setTimeout(() => {
        if (cancelled) return;
        setVariant((prev) => {
          let next = 1 + Math.floor(Math.random() * VARIANT_COUNT);
          if (next === prev) next = 1 + ((next % VARIANT_COUNT));
          return next;
        });
        scheduleNext();
      }, delay);
    };

    scheduleNext();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [reduceMotion, cycle]);

  if (reduceMotion) {
    return <NovariMark size={size} color={color} className={cn("inline-flex", className)} />;
  }

  return <div className={cn("inline-flex", className)}>{renderVariant(variant, size, color)}</div>;
}
